import { useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Trash2, Pencil, X, GraduationCap, Image as ImageIcon, DollarSign, Star, UserPlus, Palette, Archive, ArchiveRestore, MessageCircle, Home } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ImageUploadOrUrl from '../../components/ImageUploadOrUrl';
import RichEditor from '../../components/RichEditor';

const EMPTY_BENEFIT = { icon: 'ShieldCheck', title: '', description: '' };
const EMPTY_PRICE = { label: '', amount_usd: 0, note: '' };
const EMPTY_TESTIMONIAL = { name: '', role: '', photo: null, quote: '' };
const EMPTY_SPONSOR = { name: '', logo: null, url: '', tier: 'Auspiciador' };

const EMPTY = {
  title: '',
  subtitle: '',
  eyebrow: 'Certificación Internacional',
  description: '',
  cover_image: null,
  gallery: [],
  modality: 'zoom',
  starts_at: '',
  ends_at: '',
  duration: '',
  city: '',
  venue: '',
  benefits: [],
  prices: [
    { label: 'Socio CNIB', amount_usd: 0, note: 'Precio preferencial' },
    { label: 'Realtor', amount_usd: 0, note: '' },
    { label: 'No Realtor', amount_usd: 0, note: '' },
    { label: 'Extranjero', amount_usd: 0, note: '' },
  ],
  instructor: { name: '', logo: null, url: '', bio: '' },
  sponsors: [],
  testimonials: [],
  certificate_image: null,
  seats_limited: false,
  seats_quantity: null,
  whatsapp_number: '',
  whatsapp_message: 'Hola, quiero información sobre la capacitación',
  cta_mode: 'whatsapp',
  cta_url: '',
  show_on_home: false,
  primary_color: '#F5831F',
  accent_color: '#0F4C81',
  status: 'published',
  hashtag: '',
  website: '',
};

const BENEFIT_ICONS = ['ShieldCheck', 'Handshake', 'TrendingUp', 'Star', 'Award', 'Globe', 'GraduationCap', 'Target', 'Rocket', 'Users'];

export default function AdminTrainings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const galleryRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/trainings');
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (t) => {
    setEditing(t.training_id);
    setForm({
      ...EMPTY,
      ...t,
      benefits: (t.benefits || []).map((b) => ({ ...EMPTY_BENEFIT, ...b })),
      prices: (t.prices || []).map((p) => ({ ...EMPTY_PRICE, ...p })),
      testimonials: (t.testimonials || []).map((x) => ({ ...EMPTY_TESTIMONIAL, ...x })),
      sponsors: (t.sponsors || []).map((s) =>
        typeof s === 'string' ? { ...EMPTY_SPONSOR, name: s } : { ...EMPTY_SPONSOR, ...s }
      ),
      instructor: t.instructor || { name: '', logo: null, url: '', bio: '' },
    });
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form,
        seats_quantity: form.seats_limited && form.seats_quantity ? Number(form.seats_quantity) : null,
        prices: (form.prices || []).map((p) => ({ ...p, amount_usd: Number(p.amount_usd || 0) })),
      };
      if (editing) await api.patch(`/admin/trainings/${editing}`, body);
      else await api.post('/admin/trainings', body);
      toast.success('Capacitación guardada');
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta capacitación?')) return;
    await api.delete(`/admin/trainings/${id}`);
    toast.success('Eliminada');
    load();
  };

  const toggleStatus = async (t, nextStatus) => {
    await api.patch(`/admin/trainings/${t.training_id}/status`, { status: nextStatus });
    toast.success(nextStatus === 'archived' ? 'Archivada' : 'Reactivada');
    load();
  };

  const uploadGallery = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingGallery(true);
    try {
      const urls = [];
      for (const file of files) {
        if (!file.type?.startsWith('image/')) continue;
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data?.url) urls.push(data.url);
      }
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...urls].slice(0, 20) }));
    } finally { setUploadingGallery(false); }
  };

  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';
  const sectionTitle = (n, label) => (
    <h4 className="mt-5 mb-3 flex items-center gap-2 border-t border-slate-100 pt-5 font-heading text-sm font-extrabold text-slate-900">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cnib-blue text-xs text-white">{n}</span>
      {label}
    </h4>
  );

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updArr = (key, idx, patch) => setForm((f) => ({ ...f, [key]: f[key].map((x, j) => (j === idx ? { ...x, ...patch } : x)) }));
  const addArr = (key, empty) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), { ...empty }] }));
  const delArr = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, j) => j !== idx) }));

  return (
    <div className="space-y-5" data-testid="admin-trainings">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Campus · Programas</p>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Capacitaciones y certificaciones</h1>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-4 py-2 text-sm font-bold text-white hover:bg-orange-600" data-testid="admin-trainings-new">
          <Plus className="h-4 w-4" /> Nueva capacitación
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-cnib-blue" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">Aún no hay capacitaciones. Crea la primera.</div>
      ) : (
        <div className="space-y-3" data-testid="admin-trainings-list">
          {items.map((t) => (
            <div key={t.training_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-training-${t.training_id}`}>
              <div className="flex items-start gap-3">
                {t.cover_image && <img src={t.cover_image} alt="" className="h-20 w-28 shrink-0 rounded-xl object-cover" />}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${t.status === 'published' ? 'bg-emerald-100 text-emerald-700' : t.status === 'archived' ? 'bg-slate-200 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>
                      {t.status === 'published' ? 'Publicada' : t.status === 'archived' ? 'Archivada' : 'Borrador'}
                    </span>
                    <span className="rounded-full bg-cnib-blue/10 px-2 py-0.5 text-[10px] font-bold uppercase text-cnib-blue">{t.modality}</span>
                    {t.show_on_home && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-700">En Home</span>}
                  </div>
                  <p className="mt-1 truncate font-heading text-base font-extrabold text-slate-900">{t.title}</p>
                  <p className="truncate text-xs text-slate-500">{t.subtitle || t.eyebrow}</p>
                  <p className="mt-1 text-[11px] text-slate-500">
                    {t.starts_at ? String(t.starts_at).slice(0, 10) : 'Fecha a definir'} · {t.duration || 'duración s/d'}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <a href={`/capacitacion/${t.slug}`} target="_blank" rel="noopener noreferrer" className="rounded-full bg-orange-50 p-2 text-orange-600 hover:bg-orange-100" aria-label="Ver micrositio" data-testid={`admin-training-preview-${t.training_id}`}>
                    <Star className="h-4 w-4" />
                  </a>
                  {t.status === 'archived' ? (
                    <button onClick={() => toggleStatus(t, 'published')} className="rounded-full bg-emerald-50 p-2 text-emerald-600 hover:bg-emerald-100" aria-label="Reactivar"><ArchiveRestore className="h-4 w-4" /></button>
                  ) : (
                    <button onClick={() => toggleStatus(t, 'archived')} className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200" aria-label="Archivar"><Archive className="h-4 w-4" /></button>
                  )}
                  <button onClick={() => openEdit(t)} className="rounded-full bg-slate-100 p-2 text-slate-700 hover:bg-slate-200" aria-label="Editar" data-testid={`admin-training-edit-${t.training_id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(t.training_id)} className="rounded-full bg-red-50 p-2 text-red-600 hover:bg-red-100" aria-label="Eliminar"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/70 p-0 sm:items-center sm:p-6">
          <div className="w-full max-w-3xl overflow-y-auto rounded-t-3xl bg-white sm:rounded-3xl" style={{ maxHeight: '92vh' }}>
            <div className="sticky top-0 z-10 flex items-start justify-between border-b border-slate-100 bg-white px-6 py-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-orange-500">Capacitación</p>
                <h3 className="font-heading text-xl font-extrabold text-slate-900">{editing ? 'Editar capacitación' : 'Nueva capacitación'}</h3>
              </div>
              <button type="button" onClick={() => { if (window.confirm('¿Cerrar sin guardar? Se perderán todos los cambios no guardados.')) setModalOpen(false); }} className="rounded-full p-1 hover:bg-slate-100" data-testid="admin-training-close"><X className="h-5 w-5" /></button>
            </div>

            <form onSubmit={save} className="p-6">
              {sectionTitle('1', 'Identidad')}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Eyebrow (etiqueta superior)</label>
                  <input value={form.eyebrow || ''} onChange={(e) => upd({ eyebrow: e.target.value })} className={inputCls} placeholder="Certificación Internacional" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Título corto (sigla) *</label>
                  <input required value={form.title} onChange={(e) => upd({ title: e.target.value })} className={inputCls} placeholder="SRS" data-testid="admin-training-title" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Estado</label>
                  <select value={form.status} onChange={(e) => upd({ status: e.target.value })} className={inputCls}>
                    <option value="published">Publicada</option>
                    <option value="draft">Borrador</option>
                    <option value="archived">Archivada</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Subtítulo (frase comercial)</label>
                  <input value={form.subtitle || ''} onChange={(e) => upd({ subtitle: e.target.value })} className={inputCls} placeholder="Seller Representative Specialist" />
                </div>
                <div className="sm:col-span-2 flex items-center gap-2 rounded-xl bg-orange-50 p-3">
                  <input type="checkbox" id="show_home" checked={!!form.show_on_home} onChange={(e) => upd({ show_on_home: e.target.checked })} className="h-4 w-4" data-testid="admin-training-show-home" />
                  <label htmlFor="show_home" className="flex items-center gap-2 text-xs font-bold text-orange-700">
                    <Home className="h-3.5 w-3.5" /> Mostrar esta capacitación destacada en el Home
                  </label>
                </div>
              </div>

              {sectionTitle('2', 'Imagen de portada y galería')}
              <div className="space-y-3">
                <ImageUploadOrUrl value={form.cover_image} onChange={(url) => upd({ cover_image: url })} sizeHint="1600 × 900 px (hero del micrositio)" testid="admin-training-cover" />
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <label className="text-[11px] font-bold text-slate-500">Galería ({(form.gallery || []).length}/20)</label>
                    <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadGallery(Array.from(e.target.files || []))} />
                    <button type="button" onClick={() => galleryRef.current?.click()} disabled={uploadingGallery} className="inline-flex items-center gap-1 rounded-full bg-cnib-blue px-3 py-1 text-xs font-bold text-white disabled:opacity-60">
                      {uploadingGallery ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
                      Agregar fotos
                    </button>
                  </div>
                  {(form.gallery || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {form.gallery.map((url, i) => (
                        <div key={i} className="relative aspect-square overflow-hidden rounded-xl">
                          <img src={url} alt="" className="h-full w-full object-cover" />
                          <button type="button" onClick={() => upd({ gallery: form.gallery.filter((_, j) => j !== i) })} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/80 text-white">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {sectionTitle('3', 'Descripción extendida')}
              <div className="rounded-xl border border-slate-200 bg-white" data-testid="admin-training-description">
                <RichEditor value={form.description} onChange={(html) => upd({ description: html })} />
              </div>

              {sectionTitle('4', 'Modalidad, fechas y lugar')}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Modalidad *</label>
                  <select value={form.modality} onChange={(e) => upd({ modality: e.target.value })} className={inputCls}>
                    <option value="presencial">Presencial</option>
                    <option value="zoom">Vía Zoom</option>
                    <option value="hibrido">Híbrido</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Duración</label>
                  <input value={form.duration || ''} onChange={(e) => upd({ duration: e.target.value })} className={inputCls} placeholder="4 días · 20 horas académicas" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Inicio</label>
                  <input type="datetime-local" value={form.starts_at?.slice(0, 16) || ''} onChange={(e) => upd({ starts_at: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Fin</label>
                  <input type="datetime-local" value={form.ends_at?.slice(0, 16) || ''} onChange={(e) => upd({ ends_at: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Ciudad (si aplica)</label>
                  <input value={form.city || ''} onChange={(e) => upd({ city: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Sede / Venue</label>
                  <input value={form.venue || ''} onChange={(e) => upd({ venue: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2 rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="seats_limited" checked={!!form.seats_limited} onChange={(e) => upd({ seats_limited: e.target.checked })} className="h-4 w-4" data-testid="admin-training-seats-toggle" />
                    <label htmlFor="seats_limited" className="text-xs font-bold text-slate-700">Cupos limitados (mostrar "quedan X lugares")</label>
                  </div>
                  {form.seats_limited && (
                    <input type="number" min="1" value={form.seats_quantity || ''} onChange={(e) => upd({ seats_quantity: e.target.value })} className={`${inputCls} mt-2`} placeholder="Cantidad de cupos (ej. 25)" />
                  )}
                </div>
              </div>

              {sectionTitle('5', 'Beneficios y ventajas')}
              <div className="space-y-2" data-testid="admin-training-benefits">
                {(form.benefits || []).map((b, i) => (
                  <div key={i} className="grid grid-cols-[auto_1fr_30px] items-start gap-2 rounded-xl bg-slate-50 p-3 sm:grid-cols-[120px_1fr_1fr_30px]">
                    <select value={b.icon} onChange={(e) => updArr('benefits', i, { icon: e.target.value })} className={inputCls}>
                      {BENEFIT_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                    <input placeholder="Título (ej. Fortalece tus habilidades)" value={b.title} onChange={(e) => updArr('benefits', i, { title: e.target.value })} className={inputCls} />
                    <input placeholder="Descripción corta" value={b.description || ''} onChange={(e) => updArr('benefits', i, { description: e.target.value })} className={`${inputCls} col-span-2 sm:col-span-1`} />
                    <button type="button" onClick={() => delArr('benefits', i)} className="rounded-full bg-red-50 p-1.5 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => addArr('benefits', EMPTY_BENEFIT)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-cnib-blue/40 bg-cnib-blue/5 px-4 py-2 text-xs font-bold text-cnib-blue" data-testid="admin-training-benefit-add">
                  <Plus className="h-3.5 w-3.5" /> Agregar beneficio
                </button>
              </div>

              {sectionTitle('6', 'Precios (varios según público)')}
              <div className="space-y-2" data-testid="admin-training-prices">
                {(form.prices || []).map((p, i) => (
                  <div key={i} className="grid grid-cols-[1fr_110px_1fr_30px] items-center gap-2 rounded-xl bg-slate-50 p-2">
                    <input placeholder="Etiqueta (ej. Socio CNIB)" value={p.label} onChange={(e) => updArr('prices', i, { label: e.target.value })} className={inputCls} data-testid={`admin-training-price-label-${i}`} />
                    <div className="relative">
                      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400">USD</span>
                      <input type="number" min="0" step="1" value={p.amount_usd} onChange={(e) => updArr('prices', i, { amount_usd: e.target.value })} className={`${inputCls} pl-10`} />
                    </div>
                    <input placeholder="Nota opcional (ej. Precio preferencial)" value={p.note || ''} onChange={(e) => updArr('prices', i, { note: e.target.value })} className={inputCls} />
                    <button type="button" onClick={() => delArr('prices', i)} className="rounded-full bg-red-50 p-1.5 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <button type="button" onClick={() => addArr('prices', EMPTY_PRICE)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-cnib-blue/40 bg-cnib-blue/5 px-4 py-2 text-xs font-bold text-cnib-blue">
                  <DollarSign className="h-3.5 w-3.5" /> Agregar precio
                </button>
              </div>

              {sectionTitle('7', 'Instructor / Entidad certificadora')}
              <div className="grid gap-3 sm:grid-cols-2">
                <input placeholder="Nombre / Entidad (ej. NAR Realtors)" value={form.instructor?.name || ''} onChange={(e) => upd({ instructor: { ...form.instructor, name: e.target.value } })} className={inputCls} />
                <input placeholder="Sitio web (opcional)" value={form.instructor?.url || ''} onChange={(e) => upd({ instructor: { ...form.instructor, url: e.target.value } })} className={inputCls} />
                <textarea rows={2} placeholder="Bio corta (opcional)" value={form.instructor?.bio || ''} onChange={(e) => upd({ instructor: { ...form.instructor, bio: e.target.value } })} className={`${inputCls} sm:col-span-2`} />
                <div className="sm:col-span-2">
                  <ImageUploadOrUrl value={form.instructor?.logo} onChange={(url) => upd({ instructor: { ...form.instructor, logo: url } })} sizeHint="Logo instructor/entidad (PNG transparente)" testid="admin-training-instructor-logo" />
                </div>
              </div>

              {sectionTitle('8', 'Auspiciadores y Partners')}
              <p className="-mt-1 mb-3 text-[11px] text-slate-500">Solo aparecerán en el micrositio si agregas al menos uno.</p>
              <div className="space-y-3" data-testid="admin-training-sponsors">
                {(form.sponsors || []).map((sp, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 grid gap-2 sm:grid-cols-2">
                        <input placeholder="Nombre *" required value={sp.name} onChange={(e) => updArr('sponsors', i, { name: e.target.value })} className={inputCls} />
                        <select value={sp.tier || 'Auspiciador'} onChange={(e) => updArr('sponsors', i, { tier: e.target.value })} className={inputCls}>
                          <option value="Auspiciador Platino">Auspiciador Platino</option>
                          <option value="Auspiciador Oro">Auspiciador Oro</option>
                          <option value="Auspiciador Plata">Auspiciador Plata</option>
                          <option value="Auspiciador">Auspiciador</option>
                          <option value="Partner">Partner</option>
                          <option value="Media Partner">Media Partner</option>
                          <option value="Patrocinador">Patrocinador</option>
                          <option value="Aliado">Aliado</option>
                        </select>
                        <input placeholder="URL (opcional)" value={sp.url || ''} onChange={(e) => updArr('sponsors', i, { url: e.target.value })} className={`${inputCls} sm:col-span-2`} />
                        <div className="sm:col-span-2">
                          <ImageUploadOrUrl value={sp.logo} onChange={(url) => updArr('sponsors', i, { logo: url })} sizeHint="Logo PNG transparente (400 × 200 px)" testid={`admin-training-sponsor-logo-${i}`} />
                        </div>
                      </div>
                      <button type="button" onClick={() => delArr('sponsors', i)} className="rounded-full bg-red-50 p-1.5 text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => addArr('sponsors', EMPTY_SPONSOR)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-cnib-blue/40 bg-cnib-blue/5 px-4 py-2 text-xs font-bold text-cnib-blue">
                  <Plus className="h-3.5 w-3.5" /> Agregar auspiciador / partner
                </button>
              </div>

              {sectionTitle('9', 'Testimonios (opcional)')}
              <div className="space-y-3" data-testid="admin-training-testimonials">
                {(form.testimonials || []).map((t, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <input placeholder="Nombre *" required value={t.name} onChange={(e) => updArr('testimonials', i, { name: e.target.value })} className={inputCls} />
                      <input placeholder="Cargo / rol" value={t.role || ''} onChange={(e) => updArr('testimonials', i, { role: e.target.value })} className={inputCls} />
                      <textarea rows={2} placeholder="Testimonio" required value={t.quote} onChange={(e) => updArr('testimonials', i, { quote: e.target.value })} className={`${inputCls} sm:col-span-2`} />
                      <div className="sm:col-span-2">
                        <ImageUploadOrUrl value={t.photo} onChange={(url) => updArr('testimonials', i, { photo: url })} sizeHint="Foto cuadrada 400×400" testid={`admin-training-test-photo-${i}`} />
                      </div>
                    </div>
                    <button type="button" onClick={() => delArr('testimonials', i)} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-red-600">
                      <Trash2 className="h-3 w-3" /> Eliminar testimonio
                    </button>
                  </div>
                ))}
                <button type="button" onClick={() => addArr('testimonials', EMPTY_TESTIMONIAL)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-cnib-blue/40 bg-cnib-blue/5 px-4 py-2 text-xs font-bold text-cnib-blue">
                  <UserPlus className="h-3.5 w-3.5" /> Agregar testimonio
                </button>
              </div>

              {sectionTitle('10', 'Certificado de ejemplo (opcional)')}
              <ImageUploadOrUrl value={form.certificate_image} onChange={(url) => upd({ certificate_image: url })} sizeHint="Imagen del certificado que recibirán los participantes" testid="admin-training-certificate" />

              {sectionTitle('11', 'CTA de compra y WhatsApp')}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2 rounded-xl bg-slate-50 p-3">
                  <label className="mb-2 block text-[11px] font-bold text-slate-500">Modo del botón "Comprar capacitación"</label>
                  <div className="flex gap-2">
                    <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 has-[:checked]:border-orange-500 has-[:checked]:bg-orange-50">
                      <input type="radio" name="cta_mode" value="whatsapp" checked={form.cta_mode === 'whatsapp'} onChange={() => upd({ cta_mode: 'whatsapp' })} />
                      <MessageCircle className="h-4 w-4 text-emerald-600" />
                      <span className="text-xs font-bold">WhatsApp</span>
                    </label>
                    <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 has-[:checked]:border-orange-500 has-[:checked]:bg-orange-50">
                      <input type="radio" name="cta_mode" value="payment" checked={form.cta_mode === 'payment'} onChange={() => upd({ cta_mode: 'payment' })} />
                      <DollarSign className="h-4 w-4 text-cnib-blue" />
                      <span className="text-xs font-bold">Pasarela de pago (URL)</span>
                    </label>
                  </div>
                </div>
                {form.cta_mode === 'payment' && (
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-[11px] font-bold text-slate-500">URL de la pasarela de pago</label>
                    <input value={form.cta_url || ''} onChange={(e) => upd({ cta_url: e.target.value })} className={inputCls} placeholder="https://pagos.cnib.bo/..." />
                  </div>
                )}
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Número de WhatsApp (sin + ni espacios)</label>
                  <input value={form.whatsapp_number || ''} onChange={(e) => upd({ whatsapp_number: e.target.value })} className={inputCls} placeholder="59175015251" data-testid="admin-training-wa-number" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Mensaje prellenado</label>
                  <input value={form.whatsapp_message || ''} onChange={(e) => upd({ whatsapp_message: e.target.value })} className={inputCls} placeholder="Hola, quiero información sobre…" />
                </div>
              </div>

              {sectionTitle('12', 'Branding')}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500 flex items-center gap-1"><Palette className="h-3 w-3" /> Color primario</label>
                  <input type="color" value={form.primary_color || '#F5831F'} onChange={(e) => upd({ primary_color: e.target.value })} className="h-10 w-full rounded-xl border border-slate-200 bg-white" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Color de acento</label>
                  <input type="color" value={form.accent_color || '#0F4C81'} onChange={(e) => upd({ accent_color: e.target.value })} className="h-10 w-full rounded-xl border border-slate-200 bg-white" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Hashtag</label>
                  <input value={form.hashtag || ''} onChange={(e) => upd({ hashtag: e.target.value })} className={inputCls} placeholder="#SRS2026" />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-bold text-slate-500">Sitio web</label>
                  <input value={form.website || ''} onChange={(e) => upd({ website: e.target.value })} className={inputCls} placeholder="https://..." />
                </div>
              </div>

              <div className="sticky bottom-0 -mx-6 mt-6 flex items-center justify-end gap-2 border-t border-slate-100 bg-white px-6 py-4">
                <button type="button" onClick={() => { if (window.confirm('¿Cerrar sin guardar? Se perderán todos los cambios no guardados.')) setModalOpen(false); }} className="rounded-full px-5 py-2 text-sm font-bold text-slate-500">Cancelar</button>
                <button type="submit" disabled={saving} data-testid="admin-training-save" className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-2.5 text-sm font-bold text-white disabled:opacity-60">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />} Guardar capacitación
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
